import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import AuthClient from "../api/AuthClient";
import { PartyModel } from "../types";
import { usePromoterGroups } from "../hooks/useServerDataQueries";
import { getLoggedInUserId } from "../utils/authUser";
import { getSeenGroupIds, markGroupAsSeen, SeenGroupEntry } from "../utils/seenGroupsDb";

export type PromoterGroupsRequestState = "idle" | "loading" | "success" | "error";

interface PromoterGroupsContextData {
  groups: PartyModel[];
  requestState: PromoterGroupsRequestState;
  errorMessage: string;
  fetchGroups: () => Promise<void>;
  hasFetched: boolean;
  seenGroupIds: SeenGroupEntry[];
  markSeen: (id: string, partyDate: Date) => void;
}

export const PromoterGroupsContext = createContext<PromoterGroupsContextData>({
  groups: [],
  requestState: "idle",
  errorMessage: "",
  fetchGroups: async () => {},
  hasFetched: false,
  seenGroupIds: [],
  markSeen: () => {},
});

const hasOfferFromPromoter = (group: PartyModel, promoterId: string): boolean =>
  group.promotersOffers?.some(
    (promoterOffer) =>
      promoterOffer.promoter?.id === promoterId &&
      promoterOffer.offers?.length > 0
  ) ?? false;

export const PromoterGroupsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    data: groups = [],
    error,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    refetch,
  } = usePromoterGroups();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [seenGroupIds, setSeenGroupIds] = useState<SeenGroupEntry[]>([]);

  useEffect(() => {
    getSeenGroupIds()
      .then(setSeenGroupIds)
      .catch((err) => console.error("Error loading seen group ids:", err));
  }, []);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    const authClient = new AuthClient();
    const authenticationResponse = authClient.getAuthenticationResponse();
    const loggedInPromoterId = getLoggedInUserId(authenticationResponse);

    if (!loggedInPromoterId) {
      setErrorMessage("");
      return;
    }

    const groupsWithOwnOffers = groups.filter((group) =>
      hasOfferFromPromoter(group, loggedInPromoterId)
    );

    if (groupsWithOwnOffers.length === 0) {
      setErrorMessage("");
      return;
    }

    const seenEntriesFromOwnOffers = groupsWithOwnOffers.map((group) => ({
      id: group.party.id,
      partyDate: group.party.partyDate,
    }));

    seenEntriesFromOwnOffers.forEach((entry) => {
      markGroupAsSeen(entry.id, entry.partyDate).catch((err) =>
        console.error("Error persisting seen group from promoter offer:", err)
      );
    });

    setSeenGroupIds((prev) => {
      const seenIds = new Set(prev.map((entry) => entry.id));
      const nextSeenEntries = seenEntriesFromOwnOffers.filter(
        (entry) => !seenIds.has(entry.id)
      );

      if (nextSeenEntries.length === 0) {
        return prev;
      }

      return [...prev, ...nextSeenEntries];
    });
    setErrorMessage("");
  }, [groups, isSuccess]);

  useEffect(() => {
    if (!isError) {
      return;
    }

    console.error("Error fetching promoter groups:", error);
    setErrorMessage(error instanceof Error ? error.message : "Unable to fetch groups.");
  }, [error, isError]);

  const fetchGroups = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const requestState: PromoterGroupsRequestState = isLoading || isFetching
    ? "loading"
    : isError
      ? "error"
      : isSuccess
        ? "success"
        : "idle";

  const hasFetched = isSuccess || isError;

  const markSeen = useCallback((id: string, partyDate: Date) => {
    setSeenGroupIds((prev) =>
      prev.some((g) => g.id === id) ? prev : [...prev, { id, partyDate }]
    );
    markGroupAsSeen(id, partyDate).catch((err) =>
      console.error("Error persisting seen group:", err)
    );
  }, []);

  const value = useMemo(
    () => ({
      groups,
      requestState,
      errorMessage,
      fetchGroups,
      hasFetched,
      seenGroupIds,
      markSeen,
    }),
    [groups, requestState, errorMessage, fetchGroups, hasFetched, seenGroupIds, markSeen]
  );

  return (
    <PromoterGroupsContext.Provider value={value}>
      {children}
    </PromoterGroupsContext.Provider>
  );
};
